import { SimpleTable } from 'ComponentLibrary/src/piazza/tables/SimpleTable/SimpleTable'
import { SimpleTableHeadline } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableHeadline'
import {
    SimpleTableAreaCell,
    SimpleTableDateCell,
    SimpleTableMonetaryCell,
    SimpleTableNumberCell,
    SimpleTableTextCell,
    SimpleTableTrendCell,
} from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTextCells'
import {
    SimpleTableContractualOptionsCell,
    SimpleTableRentalStatusCell,
    SimpleTableUsageTypeCell,
} from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableTenantCells'
import { ColumnDef, ColumnPinningState, flexRender, OnChangeFn, Row, RowSelectionState, Table } from '@tanstack/react-table'
import { AreaValue, MonetaryValue, TenantSummaryBreakdown, tokens } from 'ComponentLibrary'
import { Box, Stack, Tooltip } from '@mui/material'
import { SimpleTableCell, SimpleTableEmptyFooter } from 'ComponentLibrary/src/piazza/tables/SimpleTable/ReadCells/SimpleTableCell'
import { StructureOption, TenantDetailsTableRow } from './types'
import { Checkbox } from 'ComponentLibrary/src/piazza/selectors'
import {
    isParkingType,
    TENANT_USAGE_TYPE_LABELS,
    TenantRentalStatusLabelKeys,
    TenantUsageTypeLabelKeys,
} from 'ComponentLibrary/src/common/tenant'
import { TenantSummarySums } from '../TenantSummaryTables/types'
import { ReactNode } from 'react'
import { TenantDiscrepancyTooltip, TenantDiscrepancyTooltipCopy, TenantDiscrepancyTooltipInfo } from '../TenantDiscrepancyTooltip'
import { IconButton, Typography } from 'ComponentLibrary/src/piazza'
import { DotOutlineIcon, PencilSimpleIcon } from '@phosphor-icons/react'
import { isEmpty } from 'lodash'

export type TenantDetailsTableCopy = {
    discrepancyTooltip: TenantDiscrepancyTooltipCopy
    discrepancyTooltipTitle: string
    discrepancyHeaders: {
        units: string
        area: string
        contractRentPerYear: string
        marketRentPerYear: string
    }
    headers: {
        id: string
        floor: string
        tenant: string
        usageType: string
        structure: string
        subUsageType: string
        leaseId: string
        status: string
        area: string
        units: string
        startDate: string
        endDate: string
        remainingLeaseTerm: string
        options: string
        contractRentPerMonth: string
        contractRentPerYear: string
        marketRentPerMonth: string
        marketRentPerYear: string
        absoluteDelta: string
        deltaPercentage: string
        comments: string
    }
    labels: {
        area: string
        vacancyArea: string
        units: string
        vacancyUnits: string
        contractRent: string
        marketRent: string
        indefinite: string
        years: string
    }
    usageTypeLabels: Partial<Record<TenantUsageTypeLabelKeys, string>>
    rentalStatusLabels: Partial<Record<TenantRentalStatusLabelKeys, string>>
    unitLabels: {
        regular: string
        parking: string
        perYear: string
        perSquareMeter: string
        perUnit: string
        perParkingSpace: string
    }
}

type TenantDetailsTableProps = {
    tenantRows: TenantDetailsTableRow[]
    getColumns?: (columns: ColumnDef<TenantDetailsTableRow>[]) => ColumnDef<TenantDetailsTableRow>[]
    columnPinning?: ColumnPinningState
    getCellError?: (row: TenantDetailsTableRow, columnId: string) => string | undefined
    structureOptions?: StructureOption[]
    disabled?: boolean
    rowSelection?: RowSelectionState
    setRowSelection?: OnChangeFn<RowSelectionState>
    virtualized?: boolean
    maxHeight?: string
    discrepancyByUsageType?: Record<
        string,
        TenantSummaryBreakdown & { calculatedSummary: TenantSummarySums; calculatedDiff: Partial<TenantSummaryBreakdown> }
    >
    selectAndOpenTenant?: (tenantSummaryItemIndex: number) => void
    copy?: TenantDetailsTableCopy
    locale?: string
}

export function TenantDetailsTable({
    tenantRows,
    getColumns,
    columnPinning = { left: ['select-col', 'reference_number', 'floor', 'tenant_name', 'usage_type'] },
    getCellError,
    structureOptions,
    disabled,
    rowSelection,
    setRowSelection,
    virtualized,
    maxHeight = virtualized ? '520px' : undefined,
    discrepancyByUsageType,
    selectAndOpenTenant,
    copy,
    locale = 'de-DE',
}: TenantDetailsTableProps) {
    const shouldHighlightDiscrepancy = (row: TenantDetailsTableRow) => {
        const calculatedDiff = row.usage_type ? discrepancyByUsageType?.[row.usage_type]?.calculatedDiff : undefined
        const discrepancyHeaders: string[] = []

        if (calculatedDiff?.number_of_units || calculatedDiff?.vacancy_number_of_units) {
            discrepancyHeaders.push(copy?.discrepancyHeaders.units ?? 'EH/STPL')
        }

        if (calculatedDiff?.total_area?.value || calculatedDiff?.vacancy_area?.value) {
            discrepancyHeaders.push(copy?.discrepancyHeaders.area ?? 'Fläche')
        }

        if (calculatedDiff?.actual_rental_income?.amount) {
            discrepancyHeaders.push(copy?.discrepancyHeaders.contractRentPerYear ?? 'Vertragsmiete P.A.')
        }

        if (calculatedDiff?.gross_rental_income?.amount) {
            discrepancyHeaders.push(copy?.discrepancyHeaders.marketRentPerYear ?? 'Marktübl. Miete P.A.')
        }

        return discrepancyHeaders
    }

    const getUsageTypeLabel = (row: TenantDetailsTableRow) =>
        row.usage_type ? (copy?.usageTypeLabels?.[row.usage_type] ?? TENANT_USAGE_TYPE_LABELS[row.usage_type]) : undefined

    const renderDiscrepancyCell = (discrepancyInfo: TenantDiscrepancyTooltipInfo | undefined, cell: ReactNode) => (
        <TenantDiscrepancyTooltip discrepancy={discrepancyInfo} copy={copy?.discrepancyTooltip}>
            <Box width="100%">{cell}</Box>
        </TenantDiscrepancyTooltip>
    )

    const renderDiscrepancyHeaderTooltip = (discrepancyHeaders: string[], cell: ReactNode) => {
        if (isEmpty(discrepancyHeaders)) {
            return <>{cell}</>
        }

        return (
            <Tooltip
                placement="top"
                title={
                    <Stack direction="column">
                        <Typography color={tokens.ColorTextInverse} variant="textS">
                            {copy?.discrepancyTooltipTitle ?? 'Spalten mit Abweichungen:'}
                        </Typography>
                        {discrepancyHeaders.map((header) => (
                            <Stack direction="row" key={header}>
                                <DotOutlineIcon
                                    style={{ marginTop: '1px' }}
                                    size={tokens.SizingIconS}
                                    color={tokens.ColorIconInverse}
                                    weight="fill"
                                />
                                <Typography color={tokens.ColorTextInverse} variant="textS">
                                    {header}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>
                }
            >
                <Box width="100%">{cell}</Box>
            </Tooltip>
        )
    }

    const shouldUsePerAreaReference = (row: TenantDetailsTableRow) => !!row.area && !(row.usage_type && isParkingType(row.usage_type))

    const getPerUnitReferenceLabel = (row: TenantDetailsTableRow) =>
        row.usage_type && isParkingType(row.usage_type)
            ? (copy?.unitLabels.perParkingSpace ?? '/Stpl.')
            : (copy?.unitLabels.perUnit ?? '/EH')

    const columns: ColumnDef<TenantDetailsTableRow>[] = [
        ...(setRowSelection
            ? [
                  {
                      id: 'select-col',
                      size: 56,
                      header: ({ table }: { table: Table<TenantDetailsTableRow> }) => (
                          <Box paddingTop={tokens.SizingSpacing200}>
                              <SimpleTableCell>
                                  <Checkbox
                                      checked={table.getIsAllRowsSelected()}
                                      indeterminate={table.getIsSomeRowsSelected()}
                                      onChange={table.getToggleAllPageRowsSelectedHandler()}
                                  />
                              </SimpleTableCell>
                          </Box>
                      ),
                      cell: ({ row }: { row: Row<TenantDetailsTableRow> }) => {
                          const discrepancyHeaders = shouldHighlightDiscrepancy(row.original)
                          const cell = (
                              <SimpleTableCell highlight={!isEmpty(discrepancyHeaders)} selected={row.getIsSelected()} hoverable>
                                  <Checkbox
                                      checked={row.getIsSelected()}
                                      disabled={!row.getCanSelect()}
                                      onChange={row.getToggleSelectedHandler()}
                                  />
                              </SimpleTableCell>
                          )
                          return renderDiscrepancyHeaderTooltip(discrepancyHeaders, cell)
                      },
                      footer: () => <SimpleTableEmptyFooter />,
                  },
              ]
            : []),
        {
            accessorKey: 'reference_number',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.id ?? 'ID'} withPadding />,
            cell: ({ row }) => {
                const discrepancyHeaders = shouldHighlightDiscrepancy(row.original)
                const cell = (
                    <SimpleTableTextCell
                        text={row.original.reference_number}
                        highlight={!isEmpty(discrepancyHeaders)}
                        selected={row.getIsSelected()}
                        hoverable
                    />
                )
                return renderDiscrepancyHeaderTooltip(discrepancyHeaders, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 70,
        },
        {
            accessorKey: 'floor',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.floor ?? 'Etage'} withPadding />,
            cell: ({ row }) => {
                const discrepancyHeaders = shouldHighlightDiscrepancy(row.original)
                const cell = (
                    <SimpleTableTextCell
                        text={row.original.floor}
                        highlight={!isEmpty(discrepancyHeaders)}
                        selected={row.getIsSelected()}
                        hoverable
                    />
                )
                return renderDiscrepancyHeaderTooltip(discrepancyHeaders, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 96,
        },
        {
            id: 'tenant_name',
            accessorFn: (row) => row.rental_agreement?.tenant_name,
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.tenant ?? 'Mieter'} withPadding />,
            cell: ({ row }) => {
                const discrepancyHeaders = shouldHighlightDiscrepancy(row.original)
                const cell = (
                    <SimpleTableTextCell
                        text={row.original.rental_agreement?.tenant_name}
                        highlight={!isEmpty(discrepancyHeaders)}
                        selected={row.getIsSelected()}
                        hoverable
                    />
                )
                return renderDiscrepancyHeaderTooltip(discrepancyHeaders, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 120,
        },
        {
            accessorKey: 'usage_type',
            header: () => (
                <Box borderRight={`${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`}>
                    <SimpleTableHeadline variant="secondary" text={copy?.headers.usageType ?? 'Nutzungsart'} withPadding />
                </Box>
            ),
            cell: ({ row }) => {
                const discrepancyHeaders = shouldHighlightDiscrepancy(row.original)
                const cell = (
                    <Box borderRight={`${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`}>
                        <SimpleTableUsageTypeCell
                            usageType={row.original.usage_type}
                            usageTypeLabels={copy?.usageTypeLabels}
                            highlight={!isEmpty(discrepancyHeaders)}
                            selected={row.getIsSelected()}
                            hoverable
                        />
                        {selectAndOpenTenant && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    right: tokens.SizingSpacing100,
                                    top: '-1px',
                                    backgroundColor: tokens.ColorBackgroundDark,
                                    borderRadius: tokens.SizingCornerS,
                                    ...tokens.Popup,
                                    visibility: 'hidden',
                                    'tr:hover &': { visibility: 'visible' },
                                }}
                            >
                                <IconButton
                                    size="small"
                                    variant="secondary"
                                    icon={<PencilSimpleIcon />}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        selectAndOpenTenant(row.index)
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                )
                return renderDiscrepancyHeaderTooltip(discrepancyHeaders, cell)
            },
            footer: () => (
                <Box borderRight={`${tokens.SizingBorderM} solid ${tokens.ColorBorderDefault}`}>
                    <SimpleTableEmptyFooter />
                </Box>
            ),
            size: 120,
        },
        ...(structureOptions && structureOptions.length > 0
            ? [
                  {
                      id: 'valuationStructureIndex',
                      accessorFn: (row: TenantDetailsTableRow) => row.valuationStructureIndex,
                      header: () => (
                          <SimpleTableHeadline variant="secondary" text={copy?.headers.structure ?? 'Bauliche Anlage'} withPadding />
                      ),
                      cell: ({ row }: { row: Row<TenantDetailsTableRow> }) => {
                          const structureIndex = row.original.valuationStructureIndex
                          const structureLabel = structureIndex !== undefined ? structureOptions[structureIndex]?.label : undefined
                          return <SimpleTableTextCell text={structureLabel} />
                      },
                      footer: () => <SimpleTableEmptyFooter />,
                      size: 149,
                  },
              ]
            : []),
        {
            accessorKey: 'sub_usage_type',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.subUsageType ?? 'Unternutzungsart'} withPadding />,
            cell: ({ row }) => <SimpleTableTextCell text={row.original.sub_usage_type} />,
            footer: () => <SimpleTableEmptyFooter />,
            size: 149,
        },
        {
            id: 'lease_number',
            accessorFn: (row) => row.rental_agreement?.lease_number,
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.leaseId ?? 'Mietvertrag-ID'} withPadding />,
            cell: ({ row }) => <SimpleTableTextCell text={row.original.rental_agreement?.lease_number} />,
            footer: () => <SimpleTableEmptyFooter />,
            size: 119,
        },
        {
            accessorKey: 'rental_status',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.status ?? 'Status'} withPadding />,
            cell: ({ row }) => (
                <SimpleTableRentalStatusCell rentalStatus={row.original.rental_status} rentalStatusLabels={copy?.rentalStatusLabels} />
            ),
            footer: () => <SimpleTableEmptyFooter />,
            size: 107,
        },
        {
            accessorKey: 'area',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.area ?? 'Fläche'} alignment="right" withPadding />,
            cell: ({ row }) => {
                const summary = row.original.usage_type ? discrepancyByUsageType?.[row.original.usage_type] : undefined
                const isVacant = row.original.rental_status === 'unrented'
                const calculatedArea = isVacant ? summary?.calculatedSummary.vacancy_area : summary?.calculatedSummary.total_area
                const diffArea = isVacant ? summary?.calculatedDiff.vacancy_area : summary?.calculatedDiff.total_area
                const summaryArea = isVacant ? summary?.vacancy_area : summary?.total_area
                const usageTypeLabel = getUsageTypeLabel(row.original)
                const categoryLabel = isVacant ? (copy?.labels.vacancyArea ?? 'Leerstandsfläche') : (copy?.labels.area ?? 'Fläche')
                const discrepancyInfo =
                    calculatedArea && diffArea?.value && summaryArea
                        ? {
                              category: `${usageTypeLabel} | ${categoryLabel}`,
                              detailsValue: AreaValue.fromDataObject(calculatedArea).format({
                                  withUnit: false,
                                  minimumNumberOfDigits: 2,
                                  locale,
                              }),
                              summaryValue: AreaValue.fromDataObject(summaryArea).format({
                                  withUnit: false,
                                  minimumNumberOfDigits: 2,
                                  locale,
                              }),
                              discrepancyValue: AreaValue.fromDataObject(diffArea).format({
                                  withUnit: false,
                                  withoutSign: true,
                                  minimumNumberOfDigits: 2,
                                  locale,
                              }),
                              unit: AreaValue.fromDataObject(calculatedArea).unitSymbol(locale),
                          }
                        : undefined
                const cell = (
                    <SimpleTableAreaCell area={row.original.area} alignment="right" highlight={!!discrepancyInfo} locale={locale} />
                )
                return renderDiscrepancyCell(discrepancyInfo, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 88,
        },
        {
            accessorKey: 'number_of_units',
            header: () => <SimpleTableHeadline variant="secondary" text={copy?.headers.units ?? 'EH/STPL'} alignment="right" withPadding />,
            cell: ({ row }) => {
                const summary = row.original.usage_type ? discrepancyByUsageType?.[row.original.usage_type] : undefined
                const calculatedDiff = summary?.calculatedDiff
                const calculatedSummary = summary?.calculatedSummary
                const isParking = row.original.usage_type && isParkingType(row.original.usage_type)
                const isVacant = row.original.rental_status === 'unrented'
                const calculatedUnits = calculatedSummary
                    ? isParking
                        ? isVacant
                            ? calculatedSummary.vacant_parking_units
                            : calculatedSummary.parking_units
                        : isVacant
                          ? calculatedSummary.vacancy_number_of_units
                          : calculatedSummary.number_of_units
                    : undefined
                const diffUnits = isVacant ? calculatedDiff?.vacancy_number_of_units : calculatedDiff?.number_of_units
                const summaryUnits = isVacant ? summary?.vacancy_number_of_units : summary?.number_of_units
                const discrepancyInfo = diffUnits
                    ? {
                          category: `${getUsageTypeLabel(
                              row.original
                          )} | ${isVacant ? (copy?.labels.vacancyUnits ?? 'Leerstand EH/Stpl.') : (copy?.labels.units ?? 'EH/Stpl.')}`,
                          detailsValue: `${calculatedUnits}`,
                          summaryValue: `${summaryUnits}`,
                          discrepancyValue: `${Math.abs(diffUnits)}`,
                          unit: isParking ? (copy?.unitLabels.parking ?? 'Stpl.') : (copy?.unitLabels.regular ?? 'EH'),
                      }
                    : undefined
                const cell = (
                    <SimpleTableNumberCell
                        number={row.original.number_of_units}
                        alignment="right"
                        highlight={!!discrepancyInfo}
                        locale={locale}
                    />
                )
                return renderDiscrepancyCell(discrepancyInfo, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 67,
        },
        {
            id: 'start_date',
            accessorFn: (row) => row.rental_agreement?.start_date,
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.startDate ?? 'Startdatum'} alignment="right" withPadding />
            ),
            cell: ({ row }) => <SimpleTableDateCell date={row.original.rental_agreement?.start_date} alignment="right" locale={locale} />,
            footer: () => <SimpleTableEmptyFooter />,
            size: 98,
        },
        {
            id: 'end_date',
            accessorFn: (row) => row.rental_agreement?.end_date,
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.endDate ?? 'Enddatum'} alignment="right" withPadding />
            ),
            cell: ({ row }) => <SimpleTableDateCell date={row.original.rental_agreement?.end_date} alignment="right" locale={locale} />,
            footer: () => <SimpleTableEmptyFooter />,
            size: 98,
        },
        {
            id: 'remainingYears',
            accessorFn: (row) => row.rental_agreement?.remaining_years,
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.remainingLeaseTerm ?? 'RLZ'} alignment="right" withPadding />
            ),
            cell: ({ row }) => {
                const end_date = row.original.rental_agreement?.end_date
                if (!end_date) {
                    return <SimpleTableTextCell text={copy?.labels.indefinite ?? 'unbef.'} alignment="right" />
                }
                return <SimpleTableNumberCell number={row.original.rental_agreement?.remaining_years} alignment="right" locale={locale} />
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 67,
        },
        {
            accessorKey: 'options',
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.options ?? 'Optionen'} alignment="right" withPadding />
            ),
            cell: ({ row }) => (
                <SimpleTableContractualOptionsCell
                    contractualOptions={row.original.rental_agreement?.options}
                    yearsLabel={copy?.labels.years}
                    alignment="right"
                />
            ),
            footer: () => <SimpleTableEmptyFooter />,
            size: 84,
        },
        {
            id: 'rent_monthly_rent_per_sqm',
            accessorFn: (row) => row.rent_details?.monthly_rent_per_sqm,
            header: () => (
                <SimpleTableHeadline
                    variant="secondary"
                    text={copy?.headers.contractRentPerMonth ?? 'Vertragsmiete P.M.'}
                    alignment="right"
                    withPadding
                />
            ),
            cell: ({ row }) => {
                return shouldUsePerAreaReference(row.original) ? (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.rent_details?.monthly_rent_per_sqm}
                        additionalUnit={copy?.unitLabels.perSquareMeter ?? '/m²'}
                        alignment="right"
                        locale={locale}
                    />
                ) : (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.rent_details?.monthly_rent_per_unit}
                        additionalUnit={getPerUnitReferenceLabel(row.original)}
                        alignment="right"
                        locale={locale}
                    />
                )
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 144,
        },
        {
            id: 'rent_yearly_rent',
            accessorFn: (row) => row.rent_details?.yearly_rent,
            header: () => (
                <SimpleTableHeadline
                    variant="secondary"
                    text={copy?.headers.contractRentPerYear ?? 'Vertragsmiete P.A.'}
                    alignment="right"
                    withPadding
                />
            ),
            cell: ({ row }) => {
                const summary = row.original.usage_type ? discrepancyByUsageType?.[row.original.usage_type] : undefined
                const calculatedSummary = summary?.calculatedSummary
                const calculatedDiff = summary?.calculatedDiff
                const discrepancyInfo = calculatedDiff?.actual_rental_income?.amount
                    ? {
                          category: `${getUsageTypeLabel(row.original)} | ${copy?.labels.contractRent ?? 'Vertragsmiete'}`,
                          detailsValue: MonetaryValue.fromMonetaryValueDataObject(calculatedSummary?.actual_rental_income)
                              .format({ withoutCurrency: true, locale })
                              .trim(),
                          summaryValue: MonetaryValue.fromMonetaryValueDataObject(summary?.actual_rental_income)
                              .format({ withoutCurrency: true, locale })
                              .trim(),
                          discrepancyValue: MonetaryValue.fromMonetaryValueDataObject(calculatedDiff.actual_rental_income)
                              .format({ withoutCurrency: true, withoutSign: true, locale })
                              .trim(),
                          unit: `${MonetaryValue.fromMonetaryValueDataObject(calculatedSummary?.actual_rental_income).currencySymbol()}${
                              copy?.unitLabels.perYear ?? '/Jahr'
                          }`,
                      }
                    : undefined
                const cell = (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.rent_details?.yearly_rent}
                        alignment="right"
                        highlight={!!discrepancyInfo}
                        locale={locale}
                    />
                )
                return renderDiscrepancyCell(discrepancyInfo, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 142,
        },
        {
            id: 'market_monthly_rent_per_sqm',
            accessorFn: (row) => row.market_rent_details?.monthly_rent_per_sqm,
            header: () => (
                <SimpleTableHeadline
                    variant="secondary"
                    text={copy?.headers.marketRentPerMonth ?? 'Marktübl. Miete P.M.'}
                    alignment="right"
                    withPadding
                />
            ),
            cell: ({ row }) => {
                return shouldUsePerAreaReference(row.original) ? (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.market_rent_details?.monthly_rent_per_sqm}
                        additionalUnit={copy?.unitLabels.perSquareMeter ?? '/m²'}
                        alignment="right"
                        locale={locale}
                    />
                ) : (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.market_rent_details?.monthly_rent_per_unit}
                        additionalUnit={getPerUnitReferenceLabel(row.original)}
                        alignment="right"
                        locale={locale}
                    />
                )
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 123,
        },
        {
            id: 'market_yearly_rent',
            accessorFn: (row) => row.market_rent_details?.yearly_rent,
            header: () => (
                <SimpleTableHeadline
                    variant="secondary"
                    text={copy?.headers.marketRentPerYear ?? 'Marktübl. Miete P.A.'}
                    alignment="right"
                    withPadding
                />
            ),
            cell: ({ row }) => {
                const summary = row.original.usage_type ? discrepancyByUsageType?.[row.original.usage_type] : undefined
                const calculatedSummary = summary?.calculatedSummary
                const calculatedDiff = summary?.calculatedDiff
                const discrepancyInfo = calculatedDiff?.gross_rental_income?.amount
                    ? {
                          category: `${getUsageTypeLabel(row.original)} | ${copy?.labels.marketRent ?? 'Marktübl. Miete'}`,
                          detailsValue: MonetaryValue.fromMonetaryValueDataObject(calculatedSummary?.gross_rental_income)
                              .format({ withoutCurrency: true, locale })
                              .trim(),
                          summaryValue: MonetaryValue.fromMonetaryValueDataObject(summary?.gross_rental_income)
                              .format({ withoutCurrency: true, locale })
                              .trim(),
                          discrepancyValue: MonetaryValue.fromMonetaryValueDataObject(calculatedDiff.gross_rental_income)
                              .format({ withoutCurrency: true, withoutSign: true, locale })
                              .trim(),
                          unit: `${MonetaryValue.fromMonetaryValueDataObject(calculatedSummary?.gross_rental_income).currencySymbol()}${
                              copy?.unitLabels.perYear ?? '/Jahr'
                          }`,
                      }
                    : undefined
                const cell = (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.market_rent_details?.yearly_rent}
                        alignment="right"
                        highlight={!!discrepancyInfo}
                        locale={locale}
                    />
                )
                return renderDiscrepancyCell(discrepancyInfo, cell)
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 121,
        },
        {
            id: 'market_delta_monthly_rent_per_sqm',
            accessorFn: (row) => row.market_rent_details?.delta_monthly_rent_per_sqm,
            header: () => (
                <SimpleTableHeadline
                    variant="secondary"
                    text={copy?.headers.absoluteDelta ?? 'Delta absolut'}
                    alignment="right"
                    withPadding
                />
            ),
            cell: ({ row }) => {
                return shouldUsePerAreaReference(row.original) ? (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.market_rent_details?.delta_monthly_rent_per_sqm}
                        additionalUnit={copy?.unitLabels.perSquareMeter ?? '/m²'}
                        alignment="right"
                        locale={locale}
                    />
                ) : (
                    <SimpleTableMonetaryCell
                        monetaryValue={row.original.market_rent_details?.delta_monthly_rent_per_unit}
                        additionalUnit={getPerUnitReferenceLabel(row.original)}
                        alignment="right"
                        locale={locale}
                    />
                )
            },
            footer: () => <SimpleTableEmptyFooter />,
            size: 113,
        },
        {
            id: 'market_delta_percentage',
            accessorFn: (row) => row.market_rent_details?.delta_percentage,
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.deltaPercentage ?? 'Delta %'} alignment="right" withPadding />
            ),
            cell: ({ row }) => (
                <SimpleTableTrendCell percentage={row.original.market_rent_details?.delta_percentage} alignment="right" locale={locale} />
            ),
            footer: () => <SimpleTableEmptyFooter />,
            size: 96,
        },
        {
            accessorKey: 'note',
            header: () => (
                <SimpleTableHeadline variant="secondary" text={copy?.headers.comments ?? 'Kommentare'} alignment="right" withPadding />
            ),
            cell: ({ row }) => <SimpleTableTextCell text={row.original.note} alignment="right" />,
            footer: () => <SimpleTableEmptyFooter />,
            size: 101,
        },
    ]

    const displayedColumns = (getColumns ? getColumns(columns) : columns).map((column): ColumnDef<TenantDetailsTableRow> => {
        if (!getCellError) {
            return column
        }
        return {
            ...column,
            cell: (context) => {
                const content = column.cell === undefined ? (context.renderValue()?.toString() ?? null) : flexRender(column.cell, context)
                const error = getCellError(context.row.original, context.column.id)
                return error ? (
                    <Box
                        role="group"
                        aria-label={error}
                        aria-invalid
                        title={error}
                        sx={{
                            backgroundColor: tokens.ColorSurfaceDangerDefault,
                            boxShadow: `inset 0 0 0 1px ${tokens.ColorBorderDanger}`,
                        }}
                    >
                        {content}
                    </Box>
                ) : (
                    content
                )
            },
        }
    })

    return (
        <SimpleTable
            data={tenantRows}
            columns={displayedColumns}
            width="100%"
            controlledSizing
            backgroundColor={tokens.ColorSurfaceDefault}
            columnPinning={columnPinning}
            disableVirtualization={!virtualized}
            maxHeight={maxHeight}
            rowSelection={rowSelection}
            setRowSelection={setRowSelection}
            disabled={disabled}
        />
    )
}
